/**
 * Is this typed company name a company we already have?
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * Measured 31 Aug: 69 leads carried a company name as loose text with no
 * company record behind it, across 44 distinct names. Lazzara Orthodontics was
 * one of them, which is why it never appeared in the CG client-link dropdown —
 * the dropdown lists company RECORDS, and there was no record to list.
 *
 * The list also held "The Pur Compoany", "eritage Elementary PTO" and
 * "Unified Construction ssrvices", alongside "Oats Overnight" and
 * "OatsOvernight" as two spellings of one client. Every one of those came from
 * the same place: a free text box with nothing to compare against.
 *
 * Kyu, 1 Sep: "to avoid company typos, especially when adding a new lead in a
 * company where there is already another older lead registered under that
 * company — auto suggest from database when typing."
 *
 * ── The rule, and why it is this one ───────────────────────────────────────
 *
 * Case, spaces and punctuation are ignored. That is exactly the rule the
 * corrective SQL pass uses, and the two have to agree: a lead created here and
 * a lead repaired by that pass must end up on the SAME company record, or the
 * cleanup quietly creates the duplicates it was run to prevent.
 *
 *     "Liquid IV"       -> liquidiv       = "Liquid I.V."
 *     "Oats Overnight"  -> oatsovernight  = "OatsOvernight"
 *     "The Pur Company" -> thepurcompany  = "The PUR Company"
 *
 * It deliberately does NOT do fuzzy matching. "SouthStream" does not match
 * "Southstream Seafoods" and "The Pur Compoany" does not match "The PUR
 * Company" — dropping or transposing letters is where a matcher starts
 * guessing, and a lead filed under the wrong client is worse than one filed
 * under none. An unlinked lead is a visible gap; a wrongly linked one is
 * invisible until it corrupts a report.
 */

export interface NamedCompany {
  id: string;
  name: string;
}

/** Case, spaces and punctuation removed. Empty for a name with no letters. */
export function companyKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * The single company this name is, or null.
 *
 * Null on no match AND on an ambiguous one. Two records that normalise the
 * same way are a data problem — two companies named "AG 1" and "AG-1" — and
 * picking whichever came first would file the lead against a coin toss. The
 * caller shows "no company record under that name yet", the lead saves with
 * the text as typed, and somebody sorts the duplicate out.
 */
export function matchCompany(typed: string, companies: NamedCompany[]): NamedCompany | null {
  const key = companyKey(typed);
  if (key === '') {
    return null;
  }
  const hits = companies.filter((entry) => companyKey(entry.name) === key);
  return hits.length === 1 ? (hits[0] ?? null) : null;
}
